import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { NotificationProvider } from "./context/NotificationContext";
import PWAInstallPrompt from "./components/pwa/PWAInstallPrompt";
import ErrorBoundary from "./components/common/ErrorBoundary";
import ConsentBanner from "./components/common/ConsentBanner";
import { trackPageView, initScrollTracking, cleanupScrollTracking } from "./utils/analyticsTracker";

// Critical pages - loaded eagerly (login flow)
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

// All other pages - lazy loaded for performance with retry on chunk load failure
const lazyRetry = (importFn) => lazy(() =>
  importFn().catch(() => {
    // Retry once after a short delay (handles deployment cache misses)
    return new Promise(resolve => setTimeout(resolve, 1500))
      .then(() => importFn())
      .catch(() => {
        // If still failing, reload the page to get fresh assets
        window.location.reload();
        return { default: () => null };
      });
  })
);

const ForgotPasswordPage = lazyRetry(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyRetry(() => import("./pages/ResetPasswordPage"));
const OnboardingPage = lazyRetry(() => import("./pages/OnboardingPage"));
const DashboardPage = lazyRetry(() => import("./pages/DashboardPage"));
const CMRGeneratorPage = lazyRetry(() => import("./pages/CMRGeneratorPage"));
const CMRHistoryPage = lazyRetry(() => import("./pages/CMRHistoryPage"));
const CMRViewPage = lazyRetry(() => import("./pages/CMRViewPage"));
const DriverHomePage = lazyRetry(() => import("./pages/DriverHomePage"));
const DriverHistoriePage = lazyRetry(() => import("./pages/DriverHistoriePage"));
const DriverSignupPage = lazyRetry(() => import("./pages/DriverSignupPage"));
const DriverSignatureSetupPage = lazyRetry(() => import("./pages/DriverSignatureSetupPage"));
const DriversPage = lazyRetry(() => import("./pages/DriversPage"));
const CustomersPage = lazyRetry(() => import("./pages/CustomersPage"));
const UploadOrderPage = lazyRetry(() => import("./pages/UploadOrderPage"));
const BlogsPage = lazyRetry(() => import("./pages/BlogsPage"));
const IncidentsPage = lazyRetry(() => import("./pages/IncidentsPage"));
const SubscriptionPage = lazyRetry(() => import("./pages/SubscriptionPage"));
const SubscriptionSuccessPage = lazyRetry(() => import("./pages/SubscriptionSuccessPage"));
const AdminPage = lazyRetry(() => import("./pages/AdminPage"));
const CompanySettingsPage = lazyRetry(() => import("./pages/CompanySettingsPage"));
const ContactPage = lazyRetry(() => import("./pages/ContactPage"));
const SuperAdminDashboard = lazyRetry(() => import("./pages/SuperAdminDashboard"));
const SuperAdminCompanies = lazyRetry(() => import("./pages/SuperAdminCompanies"));
const SuperAdminCompanyDetail = lazyRetry(() => import("./pages/SuperAdminCompanyDetail"));
const EmailVerifiedPage = lazyRetry(() => import("./pages/EmailVerifiedPage"));
const EmailVerifyFailedPage = lazyRetry(() => import("./pages/EmailVerifyFailedPage"));
const VerificationSentPage = lazyRetry(() => import("./pages/VerificationSentPage"));
const AdminPanelDashboard = lazyRetry(() => import("./pages/AdminPanelDashboard"));
const AdminPanelAccounts = lazyRetry(() => import("./pages/AdminPanelAccounts"));
const AdminPanelCompanies = lazyRetry(() => import("./pages/AdminPanelCompanies"));
const AdminPanelCmrs = lazyRetry(() => import("./pages/AdminPanelCmrs"));
const AdminPanelInvoices = lazyRetry(() => import("./pages/AdminPanelInvoices"));
const AdminPanelSubscriptions = lazyRetry(() => import("./pages/AdminPanelSubscriptions"));
const AdminPanelSettings = lazyRetry(() => import("./pages/AdminPanelSettings"));
const AdminPanelAuditLogs = lazyRetry(() => import("./pages/AdminPanelAuditLogs"));
const AdminPanelArchief = lazyRetry(() => import("./pages/AdminPanelArchief"));
const AdminPanelFeatureFlags = lazyRetry(() => import("./pages/AdminPanelFeatureFlags"));
const AdminPanelSystem = lazyRetry(() => import("./pages/AdminPanelSystem"));
const AdminPanelIncidents = lazyRetry(() => import("./pages/AdminPanelIncidents"));
const AdminPanelCompanyDetail = lazyRetry(() => import("./pages/AdminPanelCompanyDetail"));
const AdminPanelAnalytics = lazyRetry(() => import("./pages/AdminPanelAnalytics"));
const AdminPanelCampaigns = lazyRetry(() => import("./pages/AdminPanelCampaigns"));
const AdminPanelScrollHeatmap = lazyRetry(() => import("./pages/AdminPanelScrollHeatmap"));
const AdminPanelTestAccounts = lazyRetry(() => import("./pages/AdminPanelTestAccounts"));
const TestExpiredPage = lazyRetry(() => import("./pages/TestExpiredPage"));
const SavedLocationsPage = lazyRetry(() => import("./pages/SavedLocationsPage"));
// EersteStappenPage removed — replaced by DashboardTour
const FustbonPage = lazyRetry(() => import("./pages/FustbonPage"));
const FustbonHistoryPage = lazyRetry(() => import("./pages/FustbonHistoryPage"));
const DemoPage = lazyRetry(() => import("./pages/DemoPage"));
const SetupWizardPage = lazyRetry(() => import("./pages/SetupWizardPage"));

import { PageSkeleton } from './components/common/Skeletons';

// Loading spinner component - kept as fallback
const LoadingSpinner = () => <PageSkeleton />;

// Role helpers
const isDriverRole = (role) => role === 'driver';
const isCharterRole = (role) => role === 'charter';
const isPlannerRole = (role) => role === 'planner';
const isAdminRole = (role) => role === 'admin' || role === 'admin_planner' || role === 'super_admin';
const needsCompanyLink = (user) => (isDriverRole(user?.role) || isCharterRole(user?.role)) && user?.needs_company_link;

// Auth-only route — just checks login, no subscription/setup checks
const AuthOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Subscription status helpers
const needsSetup = (user) => {
  if (!user) return false;
  if (user.role === 'super_admin') return false;
  if (isDriverRole(user.role) || isCharterRole(user.role)) return false;
  if (user.is_test_account) return false; // Test accounts skip Stripe setup
  return ['onboarding', 'setup_complete'].includes(user.subscription_status);
};
const isExpiredSub = (user) => {
  if (!user) return false;
  if (user.role === 'super_admin') return false;
  if (isDriverRole(user.role) || isCharterRole(user.role)) return false;
  return user.subscription_status === 'expired';
};
const isTestExpired = (user) => {
  if (!user) return false;
  return user.is_test_account && user.subscription_status === 'test_expired';
};

// Protected Route Component - Basic authentication check
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  
  // Driver/Charter without company link: redirect to onboarding
  if (needsCompanyLink(user)) {
    return <Navigate to="/onboarding" replace />;
  }
  
  // Admin in onboarding: redirect to setup wizard
  if (needsSetup(user)) {
    return <Navigate to="/setup" replace />;
  }
  
  // Test account expired: show expiry page
  if (isTestExpired(user)) {
    return <Navigate to="/test-expired" replace />;
  }
  
  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Admin/Planner Only Route - Drivers cannot access these pages (but Charter CAN for some routes)
const AdminOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  
  // Driver/Charter without company link: redirect to onboarding
  if (needsCompanyLink(user)) {
    return <Navigate to="/onboarding" replace />;
  }
  
  // Admin in onboarding: redirect to setup wizard
  if (needsSetup(user)) {
    return <Navigate to="/setup" replace />;
  }
  
  // Expired subscription: redirect to subscription page
  if (isExpiredSub(user)) {
    return <Navigate to="/subscription" state={{ expired: true }} replace />;
  }
  
  // Drivers use the same dashboard as admins (with restricted menu)
  return children;
};

// Subscription Route - ONLY admin (company owner) can access - no drivers, no charter
const SubscriptionRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  
  // Driver/Charter without company link: redirect to onboarding
  if (needsCompanyLink(user)) {
    return <Navigate to="/onboarding" replace />;
  }
  
  // Only admin (company owner) can access subscription
  if (!isAdminRole(user.role)) {
    // Show message via URL state (toast will be shown on dashboard)
    if (isDriverRole(user.role)) {
      return <Navigate to="/dashboard" state={{ accessDenied: 'subscription' }} replace />;
    }
    return <Navigate to="/dashboard" state={{ accessDenied: 'subscription' }} replace />;
  }
  
  return children;
};

// CMR Creation Route - ADMIN, PLANNER, CHARTER, and DRIVER can all create CMRs
// Drivers need to be linked to a company first
const CMRCreationRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  
  if (needsCompanyLink(user)) {
    return <Navigate to="/onboarding" replace />;
  }
  
  if (needsSetup(user)) {
    return <Navigate to="/setup" replace />;
  }
  
  if (isExpiredSub(user)) {
    return <Navigate to="/subscription" state={{ expired: true }} replace />;
  }
  
  // All roles can create CMRs (drivers included)
  return children;
};

// Billing/Company Settings Route - ADMIN and CHARTER only (NOT Planner, NOT Driver)
const BillingRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  
  // Driver/Charter without company link: redirect to onboarding
  if (needsCompanyLink(user)) {
    return <Navigate to="/onboarding" replace />;
  }
  
  // Only ADMIN and CHARTER can access billing/company settings
  const canAccessBilling = isAdminRole(user.role) || isCharterRole(user.role);
  
  if (!canAccessBilling) {
    // Drivers go to /driver, Planners go to /dashboard
    if (isDriverRole(user.role)) {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route (redirect if logged in - drivers go to /driver, admins to /dashboard)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  
  if (user) {
    // Driver/Charter without company link: redirect to onboarding
    if (needsCompanyLink(user)) {
      return <Navigate to="/onboarding" replace />;
    }
    // Redirect based on role
    if (isDriverRole(user.role) || isCharterRole(user.role)) {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Onboarding Route - Only for drivers/charter without company link
const OnboardingRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  
  // Already linked? Redirect to dashboard
  if (!user.needs_company_link) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Super Admin Route - ONLY for whitelist Super Admin emails
// This checks role=super_admin on client side, but server also validates
const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  
  // Must be super_admin role
  if (user.role !== 'super_admin') {
    // Silent redirect - don't reveal that this route exists
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Catch-all redirect: logged-in users to dashboard, others to landing
const CatchAllRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/" replace />;
};

import AxiosErrorInterceptor from './components/common/AxiosErrorInterceptor';

/** Reset zoom to 100% on double-tap (mobile). Allows pinch-zoom but double-tap resets. */
function DoubleTapZoomReset() {
  useEffect(() => {
    let lastTap = 0;
    const handler = (e) => {
      const now = Date.now();
      if (now - lastTap < 350) {
        // Double tap detected — reset zoom
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover');
          // Re-enable zoom after reset
          requestAnimationFrame(() => {
            setTimeout(() => {
              viewport.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover');
            }, 300);
          });
        }
        e.preventDefault();
      }
      lastTap = now;
    };
    document.addEventListener('touchend', handler, { passive: false });
    return () => document.removeEventListener('touchend', handler);
  }, []);
  return null;
}

/** Track page views and scroll depth on route changes (only fires if consent was given) */
function AnalyticsPageTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
    // Fase 2: Init scroll tracking for public routes
    initScrollTracking(location.pathname);
    return () => {
      cleanupScrollTracking();
    };
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <LanguageProvider>
      <DoubleTapZoomReset />
      <AxiosErrorInterceptor />
      <AuthProvider>
        <BrowserRouter>
        <AnalyticsPageTracker />
        <NotificationProvider>
          <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/driver-signup" element={<DriverSignupPage />} />
            <Route path="/verify-email" element={<EmailVerifiedPage />} />
            <Route path="/email-verified" element={<EmailVerifiedPage />} />
            <Route path="/email-verify-failed" element={<EmailVerifyFailedPage />} />
            <Route path="/verification-sent" element={<VerificationSentPage />} />
            
            {/* Onboarding Route - For drivers to link to company */}
            <Route path="/onboarding" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />
            
            {/* Setup Wizard - Auth only, no subscription guard */}
            <Route path="/setup" element={<AuthOnlyRoute><SetupWizardPage /></AuthOnlyRoute>} />
            
            {/* Test Account Expired */}
            <Route path="/test-expired" element={<TestExpiredPage />} />
            
            {/* Admin/Owner Routes */}
            <Route path="/dashboard" element={<AdminOnlyRoute><DashboardPage /></AdminOnlyRoute>} />
            <Route path="/cmr/history" element={<AdminOnlyRoute><CMRHistoryPage /></AdminOnlyRoute>} />
            <Route path="/cmr/upload" element={<AdminOnlyRoute><UploadOrderPage /></AdminOnlyRoute>} />
            <Route path="/customers" element={<AdminOnlyRoute><CustomersPage /></AdminOnlyRoute>} />
            <Route path="/drivers" element={<AdminOnlyRoute><DriversPage /></AdminOnlyRoute>} />
            <Route path="/subscription" element={<SubscriptionRoute><SubscriptionPage /></SubscriptionRoute>} />
            <Route path="/subscription/success" element={<SubscriptionRoute><SubscriptionSuccessPage /></SubscriptionRoute>} />
            <Route path="/blogs" element={<ProtectedRoute><BlogsPage /></ProtectedRoute>} />
            <Route path="/incidents" element={<ProtectedRoute><IncidentsPage /></ProtectedRoute>} />
            
            {/* CMR Creation & Edit - Admin and Driver can create/edit */}
            <Route path="/cmr/new" element={<CMRCreationRoute><CMRGeneratorPage /></CMRCreationRoute>} />
            <Route path="/cmr/:cmrId/edit" element={<CMRCreationRoute><CMRGeneratorPage /></CMRCreationRoute>} />
            <Route path="/cmr/:cmrId" element={<ProtectedRoute><CMRViewPage /></ProtectedRoute>} />
            
            {/* Saved Locations - All authenticated users */}
            <Route path="/locations" element={<ProtectedRoute><SavedLocationsPage /></ProtectedRoute>} />
            {/* EersteStappenPage removed — replaced by DashboardTour */}

            {/* Fustbon (Packaging Slip) Routes */}
            <Route path="/fustbon/new" element={<ProtectedRoute><FustbonPage /></ProtectedRoute>} />
            <Route path="/fustbon/history" element={<ProtectedRoute><FustbonHistoryPage /></ProtectedRoute>} />
            
            {/* Public Contact Page */}
            <Route path="/contact" element={<ContactPage />} />
            
            {/* Driver Routes - Redirect to main dashboard */}
            <Route path="/driver" element={<Navigate to="/dashboard" replace />} />
            <Route path="/driver/historie" element={<Navigate to="/cmr/history" replace />} />
            <Route path="/driver/signature" element={<ProtectedRoute><DriverSignatureSetupPage /></ProtectedRoute>} />
            
            {/* Platform Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
            <Route path="/company-settings" element={<BillingRoute><CompanySettingsPage /></BillingRoute>} />
            
            {/* Super Admin Routes - HIDDEN from regular users */}
            <Route path="/superadmin" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
            <Route path="/superadmin/companies" element={<SuperAdminRoute><SuperAdminCompanies /></SuperAdminRoute>} />
            <Route path="/superadmin/companies/:companyId" element={<SuperAdminRoute><SuperAdminCompanyDetail /></SuperAdminRoute>} />
            
            {/* NEW Admin Panel Routes - Completely separate admin UI */}
            <Route path="/admin-panel" element={<SuperAdminRoute><AdminPanelDashboard /></SuperAdminRoute>} />
            <Route path="/admin-panel/accounts" element={<SuperAdminRoute><AdminPanelAccounts /></SuperAdminRoute>} />
            <Route path="/admin-panel/companies" element={<SuperAdminRoute><AdminPanelCompanies /></SuperAdminRoute>} />
            <Route path="/admin-panel/companies/:companyId" element={<SuperAdminRoute><AdminPanelCompanyDetail /></SuperAdminRoute>} />
            <Route path="/admin-panel/cmrs" element={<SuperAdminRoute><AdminPanelCmrs /></SuperAdminRoute>} />
            <Route path="/admin-panel/invoices" element={<SuperAdminRoute><AdminPanelInvoices /></SuperAdminRoute>} />
            <Route path="/admin-panel/subscriptions" element={<SuperAdminRoute><AdminPanelSubscriptions /></SuperAdminRoute>} />
            <Route path="/admin-panel/archief" element={<SuperAdminRoute><AdminPanelArchief /></SuperAdminRoute>} />
            <Route path="/admin-panel/backups" element={<SuperAdminRoute><AdminPanelArchief /></SuperAdminRoute>} />
            <Route path="/admin-panel/audit-logs" element={<SuperAdminRoute><AdminPanelAuditLogs /></SuperAdminRoute>} />
            <Route path="/admin-panel/settings" element={<SuperAdminRoute><AdminPanelSettings /></SuperAdminRoute>} />
            <Route path="/admin-panel/feature-flags" element={<SuperAdminRoute><AdminPanelFeatureFlags /></SuperAdminRoute>} />
            <Route path="/admin-panel/system" element={<SuperAdminRoute><AdminPanelSystem /></SuperAdminRoute>} />
            <Route path="/admin-panel/incidents" element={<SuperAdminRoute><AdminPanelIncidents /></SuperAdminRoute>} />
            <Route path="/admin-panel/analytics" element={<SuperAdminRoute><AdminPanelAnalytics /></SuperAdminRoute>} />
            <Route path="/admin-panel/campaigns" element={<SuperAdminRoute><AdminPanelCampaigns /></SuperAdminRoute>} />
            <Route path="/admin-panel/scroll-heatmap" element={<SuperAdminRoute><AdminPanelScrollHeatmap /></SuperAdminRoute>} />
            <Route path="/admin-panel/test-accounts" element={<SuperAdminRoute><AdminPanelTestAccounts /></SuperAdminRoute>} />
            
            {/* Catch all — redirect to dashboard if logged in, landing page if not */}
            <Route path="*" element={<CatchAllRedirect />} />
          </Routes>
          </Suspense>
          <PWAInstallPrompt />
          <ConsentBanner />
        </NotificationProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </LanguageProvider>
  );
}

// Wrap entire app with ErrorBoundary
const AppWithErrorBoundary = () => (
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

export default AppWithErrorBoundary;
